import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ColumnContainer, RowContainer } from "../../styles/containers/styles";
import { ListItem, ListItemBold, ListItemHead, ListItemQuarter, ToolTip, ToolTipContainer } from "../../styles/list/styles";
import Layout from "../layout";
import { Section, Tab, TabContainer, TabHeader, TabLink } from "./styels";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { CarIcon, DateIcon, DurationIcon, FileIcon, LocationIcon, QuestionIcon } from "../../../icons";
import WhistleblowingComments from "./whistleblowingComments";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getData } from "../../../backend/api";
import { clearTrackStatus } from "../../../store/actions/track";
import { lighten } from "polished";
import { arrayBufferToBase64, base64ToUint8Array, decryptData } from "../../functions/cryptoUtils";
import { set } from "date-fns";


const ChannelTrackPage = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { setLoaderBox, setMessage } = props;
  const trackStatus = useSelector((state) => state.trackStatus);
  const themeColors = useSelector((state) => state.themeColors);
  const [channel, setChannel] = useState(null);
  const [caseKey, setCaseKey] = useState(null);
  const [accessHash, setAccessHash] = useState(null);

  // Local state for the Decrypted Data
  const [decryptedData, setDecryptedData] = useState(null);

  // Ref to prevent double-firing or loops on the same data
  const decryptionAttempted = useRef(false);

  // Helper: Extract the specific data object to ensure stability
  const encryptedRecord = trackStatus?.data;

  // 1. Decryption Effect
  useEffect(() => {
    // A. HANDLE REFRESH / MISSING DATA
    // On Refresh, location.state is usually lost (undefined).
    // We must redirect immediately and stop execution.

    if (!(encryptedRecord && encryptedRecord._id) || !location.state?.fullPass) {
      dispatch(clearTrackStatus());
      navigate(`/channel/${id}`);
      return;
    }

    // B. STOP LOOPING
    // If we already decrypted this specific record, don't run again.
    // Also check if we already failed/attempted for this specific ID to prevent retry loops.
    if (decryptedData?._id === encryptedRecord._id || decryptionAttempted.current === encryptedRecord._id) {
        return;
    }

    const processDecryption = async () => {
      try {
        decryptionAttempted.current = encryptedRecord._id; // Mark as attempted
        setLoaderBox(true);

        const [caseId, secretKey] = location.state.fullPass.split(":");

        // 1. Verify Case ID matches
        if (encryptedRecord.caseId && encryptedRecord.caseId !== caseId) {
          throw new Error("Pass ID mismatch");
        }

        // 2. Verify Keys Exist
        if (!encryptedRecord.keys?.whistlerblower_lock) {
           throw new Error("This case is not secured for anonymous access.");
        }

        // [NEW] Calculate the Access Hash again
        const encoder = new TextEncoder();
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoder.encode(secretKey));
        const access = arrayBufferToBase64(hashBuffer);

        setAccessHash(access);

        const lock = encryptedRecord.keys.whistlerblower_lock;
        
        // --- CRYPTO LOGIC ---
        const salt = base64ToUint8Array(lock.salt);
        const iv = base64ToUint8Array(lock.iv);
        const encryptedAesKey = base64ToUint8Array(lock.ciphertext);

        const keyMaterial = await window.crypto.subtle.importKey(
          "raw", new TextEncoder().encode(secretKey), "PBKDF2", false, ["deriveKey"]
        );
        
        const wrapperKey = await window.crypto.subtle.deriveKey(
          { name: "PBKDF2", salt: salt, iterations: 100000, hash: "SHA-256" },
          keyMaterial,
          { name: "AES-GCM", length: 256 },
          false,
          ["decrypt"]
        );

        const rawMasterKey = await window.crypto.subtle.decrypt(
          { name: "AES-GCM", iv: iv },
          wrapperKey,
          encryptedAesKey
        );
        
        const masterKey = await window.crypto.subtle.importKey(
          "raw", rawMasterKey, "AES-GCM", true, ["decrypt", "encrypt"]
        );

        // [NEW] Save the key for child components
        setCaseKey(masterKey);

        // Decrypt Fields
        const encData = encryptedRecord || {};
        const title = encData.title ? await decryptData(encData.title, masterKey) : "";
        const description = encData.description ? await decryptData(encData.description, masterKey) : "";
        const whistleblowerName = encData.whistleblowerName ? await decryptData(encData.whistleblowerName, masterKey) : null;
        const whistleblowerMobile = encData.whistleblowerMobileNumber ? await decryptData(encData.whistleblowerMobileNumber, masterKey) : null;
        const whistleblowerEmail = encData.whistleblowerEmail ? await decryptData(encData.whistleblowerEmail, masterKey) : null;

        // Decrypt custom fields
        const customFields = (encData.customFields && Array.isArray(encData.customFields) && encData.customFields.length > 0) 
          ? await Promise.all(encData.customFields.map(async (item) => {
              if (item.isEncrypted && item.encryptedValue) {
                return {
                  ...item,
                  value: await decryptData(item.encryptedValue, masterKey)
                };
              }
              return item;
            })) 
          : [];

        setDecryptedData({
            ...encryptedRecord,
            title,
            description,
            whistleblowerName,
            whistleblowerMobileNumber: whistleblowerMobile,
            whistleblowerEmail,
            customFields
        });
        
        setLoaderBox(false);

      } catch (err) {
        console.error("Decryption Failed:", err);
        setLoaderBox(false);
        
        // Show error message
        setMessage({ 
            type: 1, 
            content: err.message || "Failed to decrypt data.", 
            proceed: "Okay" 
        });

        // IMPORTANT: Navigate away on failure to prevent user staring at a broken screen
        // or ensure we don't retry.
        // navigate(`/channel/${id}`); 
      }
    };

    processDecryption();

  // DEPENDENCY ARRAY FIX:
  // Only depend on 'encryptedRecord' (specific data), not the whole 'trackStatus' object.
  // We exclude 'decryptedData' to prevent circular dependencies.
  }, [encryptedRecord, location.state, id, dispatch, navigate]);

  // Fetch Channel Info (Existing logic)
  useEffect(() => {
     if (id) fetchReportingChannel(id);
  }, [id]);

  const fetchReportingChannel = async (channelId) => {
    try {
      setLoaderBox(true);
      const response = await getData({ channelId }, "channel", dispatch, navigate);
      setLoaderBox(false);
      setChannel(response.data);
    } catch (error) {
      setLoaderBox(false);
      console.error(error);
    }
  };

  // Helper function to format custom field value based on type
  const formatCustomFieldValue = (customField) => {
    const lang = sessionStorage.getItem("_lang") || "de";
    const fieldType = customField.fieldId?.fieldType || 'text';
    const rawValue = customField.value;

    if (!rawValue) return '--';

    switch (fieldType) {
      case 'date':
        try {
          const dateObj = new Date(rawValue);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          }
        } catch (e) {
          return rawValue;
        }
        return rawValue;

      case 'select':
      case 'radio':
        const options = customField.fieldId?.options || [];
        const selectedOption = options.find(opt => opt.value === rawValue);
        if (selectedOption) {
          return selectedOption.labels?.[lang] || 
                 selectedOption.labels?.de || 
                 selectedOption.value || 
                 rawValue;
        }
        return rawValue;

      case 'number':
        return Number(rawValue).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US');

      default:
        return rawValue;
    }
  };

  // Use 'decryptedData' instead of 'trackStatus.data' or 'data' variable
  const data = decryptedData;


  return (
    <div style={{width: "100%", minHeight: "calc(100vh - 40px)", backgroundColor: channel?.primaryColor ? lighten(0.35,channel?.primaryColor) : '#ffffff', padding: "20px 0"}}>
    <TabContainer style={{maxWidth: '600px'}}>
      <div>
      <TabHeader>
        <TabLink corner={channel?.elementCorners || "10px"} theme={themeColors} className="active">
          {t("Details")}
        </TabLink>
      </TabHeader>
      <Tab corner={channel?.elementCorners || "10px"} >
        <RowContainer style={{maxHeight: "none"}}>
          <ColumnContainer>
            <Section corner={channel?.elementCorners || "10px"} >
              <ListItemHead> {t("yourStatus")}</ListItemHead>
              {channel?.showStatus && <ListItem className="between"  style={{color: 'red'}}>
                <span>{t("status")}</span> {t(data?.status)}
              </ListItem>}
              <ListItem className="between" >
                <span>{t("date")}</span> {moment(data?.createdAt).format("DD.MM.YYYY")}
              </ListItem>
              <ListItem className="between" >
                <span>{t("Title")}</span> {data?.title}
              </ListItem>
              <ListItem className="between" >
                <span>{t("description")}</span> <span style={{ maxWidth:'500px', textAlign:'end', wordWrap: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap'}}>{data?.description}</span>
              </ListItem>
              <ListItem className="between">
                <span>{t("department")}</span>
                <div>
                {data?.department?.length > 0 &&
                  data.department.map((item, idx) => (
                    <span key={idx}>
                      {item.title}
                      {idx + 1 !== data.department.length && ", "}
                    </span>
                  ))}
                  </div>
              </ListItem>

              {channel?.showCategories && data?.category?._id && <ListItem className="between" >
                <span>{t("Category")}</span> {data.category[sessionStorage.getItem("_lang")]}
              </ListItem>}
              {data?.anonymity==="confidential" && <ListItem className="between" >
                <span>{t("name")}</span> {data.whistleblowerName}
              </ListItem>}
              {data?.anonymity==="confidential" && <ListItem className="between" >
                <span>{t("mobileNumber")}</span> {data.whistleblowerMobileNumber}
              </ListItem>}
              {data?.anonymity==="confidential" && <ListItem className="between" >
                <span>{t("email")}</span> {data.whistleblowerEmail}
              </ListItem>}
              {channel?.showAssignedCaseHandler && <ListItem className="between" >
                <span>{t("Case Handler")}</span> {data?.assignedCaseHandler?.fullName ? data?.assignedCaseHandler?.fullName : data?.assignedCaseHandler?.firstName ? `${data?.assignedCaseHandler?.firstName} ${data?.assignedCaseHandler?.lastName}` : t("Not Assigned")}
              </ListItem>}

              {/* Custom Fields Section */}
              {data?.customFields && data.customFields.length > 0 && (
                <>
                  {data.customFields.map((customField, index) => {
                    const lang = sessionStorage.getItem("_lang") || "de";
                    const fieldLabel = customField.fieldId?.labels?.[lang] || 
                                       customField.fieldId?.labels?.en || 
                                       customField.fieldKey || 
                                       `Field ${index + 1}`;
                    const displayValue = formatCustomFieldValue(customField);

                    return (
                      <ListItem key={`custom-${index}`} className="between">
                        <span>{fieldLabel}</span>
                        <span style={{ maxWidth:'300px', textAlign:'end', wordWrap: 'break-word', overflowWrap: 'anywhere' }}>
                          {displayValue}
                        </span>
                      </ListItem>
                    );
                  })}
                </>
              )}
            </Section>
          </ColumnContainer>
        </RowContainer>
      </Tab>
      </div>
      <div>
        <TabHeader>
        <TabLink corner={channel?.elementCorners || "10px"} theme={themeColors} >
          {"Whistleblowing " + t("comments")}
        </TabLink>
        </TabHeader>
      <Tab corner={channel?.elementCorners || "10px"}>
        <RowContainer>
          <ColumnContainer style={{width:'100%'}}>
          <WhistleblowingComments t={t} data={data} {...props} channel={channel} caseKey={caseKey} accessHash={accessHash} />
          </ColumnContainer>
        </RowContainer>
      </Tab>
      </div>
    </TabContainer>
    </div>
  );
};
// exporting the page with parent container layout..
export default Layout(ChannelTrackPage);
